import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Card, CardContent, CardActions,
  Switch, FormControlLabel, Chip, Grid, List, ListItem, ListItemIcon,
  ListItemText, Divider, CircularProgress, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import api from '../api';

const FALLBACK_PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    monthly_amount: 49,
    annual_amount: 449,
    seat_limit: 3,
    client_limit: 50,
    storage_gb: 10,
    features: ['Up to 3 staff users', 'Up to 50 clients', '10GB storage', 'Invoice management', 'E-signature (5/mo)', 'Email support'],
    display_order: 0,
  },
  {
    key: 'pro',
    name: 'Professional',
    monthly_amount: 99,
    annual_amount: 899,
    seat_limit: 10,
    client_limit: -1,
    storage_gb: 50,
    features: ['Up to 10 staff users', 'Unlimited clients', '50GB storage', 'Invoice management', 'Unlimited e-signatures', 'Workflow automation', 'Task management', 'Client portal', 'Priority support'],
    display_order: 1,
    popular: true,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    monthly_amount: 299,
    annual_amount: 2699,
    seat_limit: -1,
    client_limit: -1,
    storage_gb: 500,
    features: ['Unlimited staff users', 'Unlimited clients', '500GB storage', 'All Professional features', 'White-label branding', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'Phone support'],
    display_order: 2,
  },
];

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  const justRegistered = searchParams.get('registered') === 'true';

  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/platform/plans/')
      .then(res => {
        const fetched = res.data?.plans || res.data;
        if (Array.isArray(fetched) && fetched.length > 0) setPlans(fetched);
      })
      .catch(() => {}); // silently use fallback
  }, []);

  const handleSelectPlan = async (planKey) => {
    setCheckoutLoading(planKey);
    setError('');
    try {
      const res = await api.post('/api/platform/subscription/checkout/', {
        plan_key: planKey,
        interval: annual ? 'annual' : 'monthly',
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPrice = (plan) => annual ? plan.annual_amount : plan.monthly_amount;
  const getInterval = () => annual ? '/yr' : '/mo';

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={4}>
            Start your 14-day free trial — no credit card required
          </Typography>

          {/* Monthly/Annual Toggle */}
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Typography variant="body1" color={!annual ? 'primary' : 'text.secondary'} fontWeight={!annual ? 600 : 400}>
              Monthly
            </Typography>
            <Switch
              checked={annual}
              onChange={(e) => setAnnual(e.target.checked)}
              color="primary"
            />
            <Typography variant="body1" color={annual ? 'primary' : 'text.secondary'} fontWeight={annual ? 600 : 400}>
              Annual
            </Typography>
            {annual && (
              <Chip label="Save 20%" color="success" size="small" sx={{ fontWeight: 700 }} />
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Registered banner */}
        {justRegistered && (
          <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
            🎉 Account created! Complete your setup by activating your 14-day free trial below.
          </Alert>
        )}

        {/* Plan Cards */}
        <Grid container spacing={3} justifyContent="center" mb={8}>
          {plans.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((plan) => (
            <Grid item xs={12} md={4} key={plan.key}>
              <Card
                elevation={preselectedPlan === plan.key ? 10 : plan.popular ? 8 : 2}
                sx={{
                  borderRadius: 3,
                  border: (preselectedPlan === plan.key || plan.popular) ? '2px solid' : '1px solid',
                  borderColor: preselectedPlan === plan.key ? 'success.main' : plan.popular ? 'primary.main' : 'divider',
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {preselectedPlan === plan.key && (
                  <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                    <Chip label="✓ Your Selected Plan" color="success" sx={{ fontWeight: 700 }} />
                  </Box>
                )}
                {plan.popular && preselectedPlan !== plan.key && (
                  <Box sx={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                  }}>
                    <Chip label="Most Popular" color="primary" icon={<StarIcon />} sx={{ fontWeight: 700 }} />
                  </Box>
                )}
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>{plan.name}</Typography>

                  <Box display="flex" alignItems="baseline" mb={1}>
                    <Typography variant="h3" fontWeight={800} color="primary">
                      ${getPrice(plan)}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" ml={0.5}>
                      {getInterval()}
                    </Typography>
                  </Box>

                  {annual && (
                    <Typography variant="body2" color="success.main" mb={2}>
                      Save ${Math.round(plan.monthly_amount * 12 - plan.annual_amount)}/yr vs monthly
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <List dense disablePadding>
                    {(plan.features || []).map((feature, idx) => (
                      <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    size="large"
                    disabled={checkoutLoading === plan.key}
                    onClick={() => handleSelectPlan(plan.key)}
                    sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}
                  >
                    {checkoutLoading === plan.key ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Start Free Trial'
                    )}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer note */}
        <Box textAlign="center">
          <Typography variant="body2" color="text.secondary">
            All plans include a 14-day free trial. No credit card required to start.
            Cancel anytime. Prices in USD.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
